import type { ReactNode } from 'react'
import { useCallback, useEffect, useRef } from 'react'
import Debug from 'debug'
import { observer } from 'mobx-react-lite'
import { useIsMutating } from '@tanstack/react-query'

import { emptyTrashMutationKey } from '@jotta/grpc-connect-client/file/useTrash'
import { useBackgroundJobStore } from '@jotta/hooks/useBackgroundJobStore'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'

import { EmptyFolder } from '../EmptyFolder/EmptyFolder'
import type { FileListRenderItem } from '../FileList/FileList'
import { FileList } from '../FileList/FileList'

import type { FolderStore } from './FolderStore'
import { FolderVirtualListItem } from './FolderVirtualListItem'
const debug = Debug('jotta:files:FolderVirtualList')

export interface FolderVirtualListProps {
  store: FolderStore
  emptyContent?: ReactNode
  showEmptyFolderComponent?: boolean
  loading?: boolean
}

export const FolderVirtualList = observer<FolderVirtualListProps>(
  function FolderVirtualList({
    store,
    loading = false,
    emptyContent = (
      <EmptyFolder
        context={store.root.route.params.context}
        pathItem={store.item}
        actions={store.actions}
      />
    ),
    showEmptyFolderComponent = false,
  }) {
    const items = store.virtualListItems
    const itemCount = items.length
    const renderItem = useCallback<FileListRenderItem>(
      (index, key, viewMode) => (
        <FolderVirtualListItem key={key} index={index} store={store} />
      ),
      [store],
    )
    const isEmptyTrashOngoing =
      useIsMutating({
        mutationKey: emptyTrashMutationKey,
      }) > 0

    const disabled = store.root.route.isTrash && isEmptyTrashOngoing

    const completedJobsCount = useBackgroundJobStore(
      state => state.completedJobsCount,
    )
    const previousCompletedJobsCount = useRef(completedJobsCount)

    // Rendered only once the listing is in (pending invites can come first).
    // hasFetched in the deps makes FileList report again when it lands.
    const hasFetched = store.hasFetched
    const onRenderItemCount = useCallback(
      (count: number) => {
        if (hasFetched && count === itemCount) {
          store.hasRendered = true
        }
      },
      [store, itemCount, hasFetched],
    )

    useEffect(() => {
      if (completedJobsCount === previousCompletedJobsCount.current) {
        return
      }

      previousCompletedJobsCount.current = completedJobsCount
      store.refresh()
      //defensive refresh of the current folder when a background file operation completes in case something has changed
    }, [completedJobsCount, store])

    return (
      <>
        <FileList
          emptyContent={emptyContent}
          showEmptyContentWhenEmpty={showEmptyFolderComponent}
          itemCount={itemCount}
          renderItem={renderItem}
          viewMode={store.route.viewMode}
          disabled={disabled}
          loading={loading}
          onRenderItemCount={onRenderItemCount}
        />
        <LoadingOverlay
          id="FolderVirtualList"
          open={loading || disabled}
          position="absolute"
        />
      </>
    )
  },
)
